import { AfterViewInit, Component, HostListener, Input, OnDestroy, OnInit, ViewChild, ElementRef } from '@angular/core';
import { clone, isEmpty } from '@helpers/arrays';
import { sharedRegex } from '@helpers/spread/shared-regex';
import { EquationBar } from '@models/equation-bar';
import { EquationFunction, EquationIdentifier, FunctionGroup, IdentifierGroup } from '@models/equation-builder';
import { WindowRef } from '@progress/kendo-angular-dialog';
import { EquationsService } from '@services/equations/equations.service';
import * as ace from 'brace';
import 'brace/ext/language_tools';
import 'brace/theme/monokai';
import 'brace/theme/textmate';
import { AceComponent } from 'ngx-ace-wrapper';
import { interval, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { editorModes, editorOptions } from './editor-config';
import { beautifyEquation, EquationEvaluation, evaluateExpression, minifyEquation } from './equation-evaluation';
import { functionGroupsToIdentifiersBindings, functionsToIdentifiersBindings } from './equation-functions';
import { keypadValues } from './keypad-values';
import './lib/red';
import { reservedWords } from './reserved-words';

@Component({
  selector: 'app-equation-builder',
  templateUrl: './equation-builder.component.html',
  styleUrls: ['./equation-builder.component.scss'],
})
export class EquationBuilderComponent implements OnInit, AfterViewInit, OnDestroy {
  private destroyed$ = new Subject();
  constructor(public window: WindowRef, private equationService: EquationsService) {}
  @Input() equation: EquationBar;

  private editorContentArray: string[] = [];

  public equationValidation: EquationEvaluation = null;
  private evaluationTimeout: NodeJS.Timer = null;
  private beautifyTimeout: NodeJS.Timer = null;
  public evaluating = false;
  public beautifying = false;

  private originalEquation = '';
  private changeSource: string = null;

  // Editor
  @ViewChild('editor', { static: false }) aceEditor: AceComponent;
  @ViewChild('editorWrapper', { static: false }) editorWrapper: ElementRef;
  // public editor: ace.Editor;
  public editor: any;
  public editorContent: string;
  private cursorPos: ace.Position;
  private errorAtCharacter = -1;
  private unknownFunctions: string[] = [];
  private unknownIdentifiers: string[] = [];
  public undoManager: ace.UndoManager;
  public langTools;
  public currentTheme = 'Light';
  // public currentMode = 'red';
  public currentMode = 'red';

  // Functions
  public functionGroups: FunctionGroup[];
  public selectedFunction: EquationFunction;
  public selectedFunctions: EquationFunction[];
  public selectedFunctionGroupName: string;
  public selectedFunctionName: string;
  public selectedFunctionSyntax: string;
  public allFunctions: string[] = [];

  // Identifiers
  public identifierGroupNames: string[] = [];
  public identifiers: EquationIdentifier[] = [];
  public selectedIdentifier: EquationIdentifier;
  public selectedIdentifierGroup: IdentifierGroup;
  public selectedIdentifiers: EquationIdentifier[] = [];
  public selectedIdentifierName: string;
  public selectedIdentifierGroupName: string;
  public allIdentifiers: string[] = [];
  // private startBeautification = debounce(function() {
  //   const { row, column } = this.editor.selection.lead;
  //   this.cursorPos = this.editor.selection.getCursor();
  //   console.log(`💮 Before: ${JSON.stringify(this.cursorPos)}\n${JSON.stringify(this.editorContent)}`);
  //   this.editorContent = beautifyEquation(this.editorContent);
  //   console.log(`💮 After: ${JSON.stringify(this.cursorPos)}\n${JSON.stringify(this.editorContent)}`);

  //   if (this.beautifyTimeout) {
  //     clearTimeout(this.beautifyTimeout);
  //     this.beautifyTimeout = null;
  //   }

  //   this.beautifyTimeout = setTimeout(() => this.editor.moveCursorTo(row, column));
  // }, 750);

  @HostListener('keydown', ['$event'])
  public keydown(event: any): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.onEscapePressed(event);
    }
  }


  ngOnInit() {
    this.originalEquation = this.equation.content;
    this.editorContent = beautifyEquation(this.equation.content.trim());    
    this.getFunctionsAndIdentifiers();
  }

  private resizeObserver: ResizeObserver;

  ngAfterViewInit() {
    // Editor will be initialized on-demand when needed
    this.editorContentArray = this.editorContent.split(/\n/);
    this.setupResizeObserver();
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  private setupResizeObserver(retries = 10) {
    const kendoContent = document.querySelector('.k-window-content');
    if (kendoContent && 'ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.editor) {
          this.editor.resize();
          this.editor.session.setWrapLimitRange(null, null);
        }
      });
      this.resizeObserver.observe(kendoContent);
    } else if (retries > 0) {
      setTimeout(() => this.setupResizeObserver(retries - 1), 100);
    }
  }

  private startEvaluation = () => {
    this.evaluating = true;

    if (this.evaluationTimeout) {
      clearTimeout(this.evaluationTimeout);
      this.evaluationTimeout = null;
    }
    this.evaluationTimeout = setTimeout(() => {
      // FIX: Only skip validation if content is truly empty (no meaningful content)
      if (!this.editorContent || this.editorContent.trim() === '') {
        this.equationValidation = {
          valid: true,
          message: 'No syntax errors detected',
          expression: '',
          index: null
        };
        setTimeout(() => (this.evaluating = false), 500);
        return;
      }
      
      this.equationValidation = evaluateExpression(this.editorContent, this.allFunctions, this.allIdentifiers);
      setTimeout(() => (this.evaluating = false), 500);
    }, 500);
  };

  private startBeautification = () => {
    this.beautifying = true;

    if (this.beautifyTimeout) {
      clearTimeout(this.beautifyTimeout);
      this.beautifyTimeout = null;
    }
    this.beautifyTimeout = setTimeout(() => {
      // Check if editor is initialized
      if (!this.editor) {
        this.beautifying = false;
        return;
      }
      
      // const { row, column } = this.editor.selection;
      this.cursorPos = this.editor.selection.getCursor();
      // console.log(`💮 Before: ${JSON.stringify(this.cursorPos)}\n${JSON.stringify(this.editorContent)}`);

      const beautified = beautifyEquation(this.editorContent);
      if (this.editorContent !== beautified) {
        this.editorContent = beautified;
        // console.log(`💮 After: ${JSON.stringify(this.cursorPos)}\n${JSON.stringify(this.editorContent)}`);
      }

      setTimeout(() => (this.beautifying = false), 500);

      // this.editor.moveCursorTo(row, column);
    }, 500);
  };

  setupEditor() {
    ace.acequire('ace/theme/textmate');
    ace.acequire('./lib/red');
    this.langTools = ace.acequire('ace/ext/language_tools');
    // No direct ACE API calls needed
    // All configuration should be handled via Angular bindings and [config] if needed
    // No need to set options, blockScrolling, or focus
    // No need to assign undoManager
    // No need for interval for wrap limit (unless you want to keep that logic for another reason)
  }

  // readjusts the wrapLimit based on the windows width
  private autoFitTextWrapLimit() {
    if (!this.editor) {
      return;
    }
    if (this.editor.getSession().getUseWrapMode()) {
      const contentWidth: number = Math.round(Number(this.editor.container.ownerDocument.querySelector('.ace_scroller').clientWidth));
      const characterWidth: number = Math.round(Number(this.editor.renderer.characterWidth));
      const newLimit = Math.round(contentWidth / characterWidth);
      this.editor.getSession().setWrapLimit(newLimit);
    }
  }

  // getEquationFunctions() {
  //   this.functionGroups = functionGroups;
  // }

  getFunctionsAndIdentifiers() {
    this.equationService
      .getEquationIdentifiers()
      .pipe(take(1))
      .subscribe(data => {
        this.identifiers = data['equations'][0];
        this.functionGroups = data['functions'];
        this.identifierGroupNames = Object.keys(this.identifiers);

        this.identifierGroupNames.forEach(key => {
          const jsonString = this.identifiers[key];
          try {
            this.identifiers[key] = isEmpty(jsonString) ? [] : JSON.parse(jsonString);
          } catch (e) {
            console.error(`Error parsing ${key}: `, e);
            console.log({ jsonString });
            this.identifiers[key] = [];
          }
        });

        // Adding Liquidity Source Types to the Mnemonics list
        this.identifiers['Liquidity Sources Mnemonics'].push(
          ...[
            { name: 'Primary', desc: 'Primary', syntax: 'Primary', example: 'Primary' },
            { name: 'Secondary', desc: 'Secondary', syntax: 'Secondary', example: 'Secondary' },
            { name: 'Tertiary', desc: 'Tertiary', syntax: 'Tertiary', example: 'Tertiary' },
          ]
        );

        // Adding Reserved Words
        this.identifiers['Reserved Words'] = reservedWords;
        this.identifierGroupNames.push('Reserved Words');

        // Merge Balance Sheet and Global Account together.
        if (
          this.identifierGroupNames.includes('Balance Sheet Mnemonics') &&
          this.identifierGroupNames.includes('Global Account Mnemonics')
        ) {
          // Remove spaces and special characters from 'Global Account Mnemonics'
          this.identifiers['Global Account Mnemonics'].forEach((acc: EquationIdentifier) => {
            acc.name = acc.name.replace(/\W/g, '');
          });

          // Remove 'Global Account Mnemonics' from identifier groups.
          const idx = this.identifierGroupNames.indexOf('Global Account Mnemonics');

          this.identifiers['Balance Sheet Mnemonics'] = [
            ...this.identifiers['Global Account Mnemonics'],
            ...this.identifiers['Balance Sheet Mnemonics'],
          ];

          this.identifierGroupNames.splice(idx, 1);
          delete this.identifiers['Global Account Mnemonics'];
        }

        this.selectedFunctionGroupName = this.functionGroups[0].name;
        this.selectedIdentifierGroupName = this.identifierGroupNames[0];
        this.getAllFunctionsAndIdentifiers();
        this.onFunctionGroupSelected();
        this.onIdentifierGroupSelected();
        this.createAutocompleteList();
        
        // FIX: Delay the initial evaluation to ensure editorContent is properly set
        setTimeout(() => {
          this.startEvaluation();
        }, 100);
      });
  }

  private createAutocompleteList() {
    if (!this.langTools) {
      this.langTools = ace.acequire('ace/ext/language_tools');
    }
    interface AutocompleteEntry {
      name: string;
      value: string;
      score: number;
      meta: string;
    }

    const abbreviate = str =>
      str
        .replace(/Mnemonics/gi, '')
        .replace(/Liquidity/gi, 'Liq.')
        .replace(/Balance/gi, 'Bal.')
        .replace(/Investments/gi, 'Invests.')
        .replace(/Functions/gi, 'Functs.')
        .replace(/Liabilities/gi, 'Liabs.');

    const autoCompleteValues: AutocompleteEntry[] = [];
    this.identifierGroupNames.forEach((group: string) => {
      this.identifiers[group].forEach((identifier: EquationIdentifier) => {
        let meta = clone(group);
        meta = abbreviate(meta);

        autoCompleteValues.push({
          name: identifier.name,
          value: identifier.name,
          score: Infinity,
          meta,
        });
      });
    });

    this.functionGroups.forEach((group: FunctionGroup) => {
      group.functions.forEach(funct => {
        let meta = clone(group.name);
        meta = abbreviate(meta);

        autoCompleteValues.push({
          name: funct.name,
          value: funct.name,
          score: Infinity,
          meta,
        });
      });
    });

    const customCompleter = {
      getCompletions: (editor, session, pos, prefix, callback) => {
        callback(null, autoCompleteValues);
      },
    };
    this.langTools.addCompleter(customCompleter);
  }

  private getAllFunctionsAndIdentifiers() {
    // Functions
    this.functionGroups.forEach(group => {
      group.functions.forEach(funct => {
        // Get function name without the (Mn)
        const functName = funct.name.toLowerCase().match(/\w+/)[0];
        if (!this.allFunctions.includes(functName)) {
          this.allFunctions.push(functName);
        }
      });
    });

    // Identifiers
    this.identifierGroupNames.forEach((group: string) => {
      this.identifiers[group].forEach((identifier: EquationIdentifier) => {
        if (!this.allIdentifiers.includes(identifier.name.toLowerCase())) {
          this.allIdentifiers.push(identifier.name.toLowerCase());
        }
      });
    });
  }

  private initializeEditor() {
    if (this.aceEditor) {
      // Check the directiveRef property specifically
      if (this.aceEditor.directiveRef) {
        // Try to access the editor through directiveRef
        const directive = this.aceEditor.directiveRef;
        this.editor = directive.ace?.() || (directive as any).getEditor?.() || (directive as any).editor || (directive as any)._editor;
        
        if (this.editor) {
          this.undoManager = this.editor.getSession().getUndoManager();
          this.setupEditor();
          return true;
        }
      }
      
      // Try different ways to access the editor instance as fallback
      this.editor = (this.aceEditor as any).editor || 
                   (this.aceEditor as any)._editor || 
                   (this.aceEditor as any).aceEditor ||
                   (this.aceEditor as any).ace ||
                   (this.aceEditor as any).getEditor?.() ||
                   (this.aceEditor as any).getAceEditor?.() ||
                   (this.aceEditor as any).editorInstance;
      
      if (this.editor) {
        this.undoManager = this.editor.getSession().getUndoManager();
        this.setupEditor();
        return true;
      }
    }
    
    return false;
  }

  private editorInitialized = false;

  public addToEditor(text: string) {
    // Check if editor is initialized, try to initialize if not
    if (!this.editor) {
      this.editorInitialized = true;
      if (!this.initializeEditor()) {
        console.warn('Editor not yet initialized');
        return;
      }
    }

    const range: ace.Range = this.editor.selection.getRange();
    const existingSelection = range.start.row !== range.end.row || range.start.column !== range.end.column;
    if (existingSelection && text.match(/^(?:[\(\)])$/)) {
      const selectedText = this.editor.getSelectedText();
      this.editor.session.replace(range, `(${selectedText})`);
    } else {
      if (text === '(') {
        this.editor.session.replace(range, '()');
        setTimeout(() => this.editor.navigateLeft(1), 10);
      } else {
        this.editor.session.replace(range, text);
      }
    }
    this.cursorPos = this.editor.selection.getCursor();
  }

  public undo() {
    if (!this.undoManager) {
      return;
    }
    if (this.undoManager.hasUndo()) {
      this.changeSource = 'event';
      this.undoManager.undo();
    }
  }

  public newMode() {
    this.currentMode = editorModes[Math.floor(Math.random() * editorModes.length)];
  }

  public validateEquation() {
    beautifyEquation(this.editorContent);
  }

  //
  //  Event-handler Methods
  //

  public onFunctionSelected() {
    const findFunction = this.selectedFunctions.find(f => f.name === this.selectedFunctionName);
    this.selectedFunctionSyntax = findFunction ? findFunction.syntax : 'not found';
    this.selectedFunction = findFunction;

    const findBinding = functionsToIdentifiersBindings.find(b => b.function === this.selectedFunctionName);
    if (findBinding) {
      this.selectedIdentifierGroupName = findBinding.identifierGroup;
      this.onIdentifierGroupSelected();
    }
  }

  public onFunctionGroupSelected() {
    const findFunctionGroup = this.functionGroups.find(fg => fg.name === this.selectedFunctionGroupName);
    if (findFunctionGroup) {
      this.selectedFunctions = findFunctionGroup.functions;
      if (this.selectedFunctions.length > 0) {
        this.selectedFunctionName = this.selectedFunctions[0].name;
        this.onFunctionSelected();
      }

      const findBinding = functionGroupsToIdentifiersBindings.find(b => b.functionGroup === this.selectedFunctionGroupName);
      if (findBinding) {
        this.selectedIdentifierGroupName = findBinding.identifierGroup;
        this.onIdentifierGroupSelected();
      }
    } else {
      this.selectedFunctions = [];
    }
  }

  public onIdentifierSelected() {
    const findIdentifier = this.selectedIdentifiers.find(i => i.name === this.selectedIdentifierName);
    this.selectedIdentifier = findIdentifier;
  }

  public onIdentifierGroupSelected() {
    const findIdentifierGroup = this.identifiers[this.selectedIdentifierGroupName];
    if (findIdentifierGroup) {
      this.selectedIdentifiers = findIdentifierGroup;
      if (this.selectedIdentifiers.length > 0) {
        this.selectedIdentifier = this.selectedIdentifiers[0];
        this.selectedIdentifierName = this.selectedIdentifiers[0].name;
        this.onIdentifierSelected();
      }
    } else {
      this.selectedIdentifiers = [];
    }
  }

  public onEditorChange(event) {
    // this.changeSource is used to avoid automatic self-triggering of 'onEditorChanged'

     //console.log({ evaluating: this.evaluating, beautifying: this.beautifying });

    // BUG FIX 93829: Update editorContent from the event to ensure validation works correctly
    // The issue was that editorContent was not being updated when typing, causing validation
    // to always see empty content even when there was actual text in the editor
    if (event && typeof event === 'string') {
      this.editorContent = event;
    } else if (this.editor) {
      // Fallback: get value from editor
      this.editorContent = this.editor.getValue();
    }

    if (!this.beautifying && !this.evaluating) {
      if (this.allFunctions.length > 0 || this.allIdentifiers.length > 0) {
        this.startEvaluation();
        // this.startBeautification();
      }
    }

    this.changeSource = null;

    // if (this.allFunctions.length > 0 || this.allIdentifiers.length > 0) {
    //   this.evaluating = true;
    //   this.startEvaluation();
    // }

    // if (this.changeSource === 'event') {
    //   this.changeSource = null;
    //   return;
    // }

    // this.startBeautification();

    // if (this.changeSource === null || this.changeSource === 'keypad') {
    //   this.changeSource = 'event';
    // } else {
    //   this.changeSource = null;
    // }

    this.editorContentArray = this.editorContent.split(/\n/);
  }

  onChange() {
    if (!this.editor && !this.editorInitialized) {
      this.editorInitialized = true;
      if (!this.initializeEditor()) {
        return;
      }
    } else if (!this.editor) {
      return;
    }
    
    setTimeout(() => {
      this.editorContent = this.editor.getValue();
      this.onEditorChange(null);
    },500);
  }

  // Event when editor gains focus
  onFocus() {
    if (!this.editor && !this.editorInitialized) {
      this.editorInitialized = true;
      if (!this.initializeEditor()) {
        return;
      }
    } else if (!this.editor || !this.cursorPos) { 
      return;
    }
    
    const { row, column } = this.cursorPos;
    setTimeout(() => {
      this.editor.navigateTo(row, column);
    }, 200);
  }

  // Event when editor loses focus
  onBlur() {
    if (!this.editor && !this.editorInitialized) {
      this.editorInitialized = true;
      if (!this.initializeEditor()) {
        return;
      }
    } else if (!this.editor) {
      return;
    }
    
    this.cursorPos = this.editor.selection.getCursor();
  }

  public onFunctionDoubleClick(event) {
    if (!this.selectedFunction) {
      return;
    }
    this.addToEditor(this.selectedFunction.name);
  }

  public onIdentifierDoubleClick(event) {
    if (!this.selectedIdentifier) {
      return;
    }
    this.addToEditor(this.selectedIdentifier.name);
  }

  public onClearClicked() {
    this.editorContent = '';
  }

  public onKeypadPressed(event) {
    const divId = event.target.id;
    if (keypadValues.hasOwnProperty(divId)) {
      this.changeSource = 'keypad';
      this.addToEditor(keypadValues[divId]);
    }
  }

  public onOkClicked(event) {
    this.changeSource = 'ok';
    // Ensure editor is initialized and get the latest value
    if (!this.editor) {
      this.initializeEditor();
    }
    
    if (this.editor) {
      this.editorContent = this.editor.getValue();
    }
    const result = { equationSaved: true, equation: minifyEquation(this.editorContent) };
    this.window.close(result);
  }

  public onCancelClicked(event) {
    // this.editor.setValue(minifyEquation(this.originalEquation));
    this.changeSource = 'cancel';
    const result = { equationSaved: false, equation: this.originalEquation };
    // console.log(`Result from 'onCancelClicked': ${JSON.stringify(result)} `);
    this.window.close(result);
  }

  public onEscapePressed(event) {
    event.preventDefault();
    this.onCancelClicked(event);
  }

  public isErrorAtCharacter() {
    const match = this.equationValidation.message.match(/character (\d+)/);
    if (match && match[1]) {
      this.errorAtCharacter = Number(match[1]) + 1;
      return true;
    }
    this.errorAtCharacter = -1;
    return false;
  }

  public isUnknownFunction() {
    const matchUnknownFunctions = this.equationValidation.message.match(/Unknown functions?: (.+)/);
    this.unknownFunctions = [];
    if (matchUnknownFunctions && matchUnknownFunctions[1]) {
      const functions = matchUnknownFunctions[1];
      const re = new RegExp(sharedRegex.equationBuilderRegEx);
      let match = null;

      do {
        match = re.exec(functions);
        if (match && match[1]) {
          this.unknownFunctions.push(match[1]);
        }
      } while (match);

      return true;
    }
    return false;
  }
  public isUnknownIdentifier() {
    const matchUnknownIdentifiers = this.equationValidation.message.match(/Unknown identifiers?: (.+)/);
    this.unknownIdentifiers = [];
    if (matchUnknownIdentifiers && matchUnknownIdentifiers[1]) {
      const identifiers = matchUnknownIdentifiers[1];
      const re = new RegExp(sharedRegex.equationBuilderRegEx);
      let match = null;

      do {
        match = re.exec(identifiers);
        if (match && match[1]) {
          this.unknownIdentifiers.push(match[1]);
        }
      } while (match);

      return true;
    }
    return false;
  }

  public onValidationClicked(event) {
    if (!this.editor) {
      return;
    }
    if (this.errorAtCharacter > 0) {
      this.cursorPos.column = this.errorAtCharacter;
    }
    if (this.unknownIdentifiers.length > 0) {
      const pos = this.editorContent.search(this.unknownIdentifiers[0]);
      if (pos >= 0) {
        this.cursorPos.column = pos + this.unknownIdentifiers[0].length;
      }
    } else if (this.unknownFunctions.length > 0) {
      const pos = this.editorContent.search(this.unknownFunctions[0]);
      if (pos >= 0) {
        this.cursorPos.column = pos + this.unknownFunctions[0].length;
      }
    }
  }

  public editorTheme: string = 'textmate';

  public toggleTheme() {
    if (this.editorTheme === 'textmate') {
      this.editorTheme = 'monokai';
      this.currentTheme = 'Dark';
    } else {
      this.editorTheme = 'textmate';
      this.currentTheme = 'Light';
    }
  }

  public aceConfig = { showGutter: false,wrap:true,showPrintMargin:false,indentedSoftWrap:false };

  public triggerAceResize(retries = 5) {
    if (!this.editor) {
      this.initializeEditor(); // Try to initialize if not already done
    }
    if (this.editor) {
      this.editor.resize();
      this.editor.session.setWrapLimitRange(null, null);
    } else if (retries > 0) {
      setTimeout(() => this.triggerAceResize(retries - 1), 100);
    }
  }
}
